import { useState } from 'react';
import { useReglages } from '../../api/reglages.jsx';
import { ScaleFit } from '../../components/ScaleFit.jsx';
import { SOC } from '../../constants/company';
import { PAYS_BY } from '../../constants/countries';
import { ACCESS_ROLE } from '../../lib/access';
import { DEVISE, fmt, fmtH } from '../../lib/currency';
import { tr, localeDates } from '../../lib/i18n';
import { printDoc } from '../../lib/print';
import { lignesDuPays, tauxRetenues } from '../../lib/charges';
import { retenuesSalariales, tauxEcrit } from '../../lib/retenues';
import { hours } from '../../lib/utils';
import { C, docAcc } from '../../theme/palette';
import { bilanDuMois, fmtEcart, heuresDuContrat } from '../../lib/pointage';
import { joursDuMois } from '../../lib/jours-du-mois';
import { nomCourtDuJour } from '../../lib/semaine';
import {
  LIEUX_SANS_CHANTIER,
  CLE_TELETRAVAIL_ACCEPTE,
  constatDuBadge,
  libelleLieu,
  marqueDuConstat,
} from '../../lib/lieux-travail';

export function FicheMensuelle({
  emp,
  chantiers,
  soc,
  onClose,
  libelleLieu: titreLieu = 'Chantier',
}) {
  const [moisSel, setMoisSel] = useState('');
  const { reglageJson } = useReglages();
  const acceptes = reglageJson(CLE_TELETRAVAIL_ACCEPTE, []) || [];
  if (!emp) return null;
  const _da = docAcc(soc),
    accInk = _da.accInk;
  const vsSal =
    ACCESS_ROLE === 'patron' ||
    ACCESS_ROLE === 'comptable' ||
    ACCESS_ROLE === 'ouvrier' ||
    (soc?.permSalaire || {})[ACCESS_ROLE] === true;
  const now = new Date();
  const dateEmis = now.toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const normK = (d) => {
    d = (d || '') + '';
    if (d.includes('-')) {
      const z = d.split('-');
      return {
        ym: z[0] + '-' + (z[1] || '').padStart(2, '0'),
        k: z[0] + (z[1] || '').padStart(2, '0') + (z[2] || '').padStart(2, '0'),
        l: (z[2] || '').padStart(2, '0') + '.' + (z[1] || '').padStart(2, '0') + '.' + z[0],
      };
    }
    const p = d.split('.');
    if (p.length >= 3)
      return {
        ym: p[2] + '-' + (p[1] || '').padStart(2, '0'),
        k: p[2] + (p[1] || '').padStart(2, '0') + (p[0] || '').padStart(2, '0'),
        l: (p[0] || '').padStart(2, '0') + '.' + (p[1] || '').padStart(2, '0') + '.' + p[2],
      };
    return { ym: '', k: d, l: d };
  };
  const rows = [];
  // Au bureau, au dépôt, en télétravail : le constat suit le lieu (« ✓ sur
  // place », « ⚠ hors du lieu », « à accepter »).
  const avecMarque = (nom, p) => {
    const marque = marqueDuConstat(constatDuBadge(p, emp, acceptes));
    return marque ? nom + ' · ' + marque : nom;
  };
  const avecLieux = [
    ...(chantiers || []).flatMap((c) => c.pointages || []),
    ...(emp.badges || []),
  ].some((p) => LIEUX_SANS_CHANTIER.includes(p.lieu));
  (chantiers || []).forEach((c) => {
    (c.pointages || [])
      .filter((p) => p.empId === emp.id)
      .forEach((p) => {
        const z = normK(p.date);
        rows.push({
          ym: z.ym,
          k: z.k,
          date: z.l,
          chantier: avecMarque(c.nom, p),
          h: hours(p.entree, p.sortie, p.pause),
          e: p.entree || '',
          s: p.sortie || '',
          pause: +p.pause || 0,
          // Les travaux notés au départ du chantier.
          note: p.tache || '',
        });
      });
    (c.rapports || [])
      .filter((r) => (r.empId != null ? r.empId === emp.id : r.empNom === emp.nom))
      .forEach((r) => {
        const z = normK(r.date);
        const ex = rows.find((x) => x.k === z.k && x.chantier === c.nom);
        if (ex) {
          // Le rapport du jour complète les travaux du pointage.
          if (r.contenu && !ex.note.includes(r.contenu))
            ex.note = ex.note ? ex.note + ' · ' + r.contenu : r.contenu;
          if (!ex.h && r.heures) ex.h = r.heures;
        } else
          rows.push({
            ym: z.ym,
            k: z.k,
            date: z.l,
            chantier: c.nom,
            h: r.heures || 0,
            e: r.hd || '',
            s: r.hf || '',
            note: r.contenu || '',
          });
      });
  });
  // Les heures pointées sur sa fiche, sans chantier (atelier, dépôt) : elles
  // comptaient au relevé de l'équipe et dans son espace, mais manquaient ici.
  // L'écran Équipe les passe déjà comme un lieu (« Magasin », « Sans
  // chantier ») : chaque journée n'est comptée qu'une fois.
  const dejaComptes = new Set(
    (chantiers || [])
      .flatMap((c) => c.pointages || [])
      .map((p) => p.id)
      .filter((id) => id != null),
  );
  (emp.badges || [])
    .filter((b) => b.chId == null && b.type !== 'absence' && !dejaComptes.has(b.id))
    .forEach((b) => {
      const z = normK(b.date);
      rows.push({
        ym: z.ym,
        k: z.k,
        date: z.l,
        // Le bureau, le dépôt ou la maison : le lieu plutôt qu'un tiret.
        chantier: LIEUX_SANS_CHANTIER.includes(b.lieu) ? avecMarque(libelleLieu(b.lieu), b) : '—',
        h: hours(b.entree, b.sortie, b.pause),
        e: b.entree || '',
        s: b.sortie || '',
        pause: +b.pause || 0,
        note: b.note || '',
      });
    });
  const moisDispo = [...new Set(rows.map((r) => r.ym).filter(Boolean))].sort().reverse();
  const curYM =
    moisSel ||
    moisDispo[0] ||
    now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  const moisRows = rows
    .filter((r) => r.ym === curYM)
    .sort((a, b) => (a.k || '').localeCompare(b.k || ''));
  const totalH = moisRows.reduce((a, r) => a + (r.h || 0), 0);
  // Le contrat donne la journée prévue ; l'employé note chaque jour ce qu'il a
  // fait. L'écart dit les heures sup et les départs plus tôt.
  const bilan = bilanDuMois(moisRows, emp);
  // Le détail imprimé : chaque jour ouvrable du pays (lundi à vendredi en
  // Suisse), du 1er à la fin du mois, même sans heures ; les jours de repos
  // quand on y a travaillé.
  const lignesJour = joursDuMois(curYM, moisRows, soc?.pays);
  const dernierDuJour = (i) =>
    i === lignesJour.length - 1 || lignesJour[i + 1].k !== lignesJour[i].k;
  const jourCourt = (r) => {
    if (r.jourSemaine == null) return '';
    const [a, m, j] = [r.k.slice(0, 4), r.k.slice(4, 6), r.k.slice(6, 8)].map(Number);
    return nomCourtDuJour(new Date(a, m - 1, j), localeDates());
  };
  const txH = emp.txH || 0;
  // Sans taux horaire, le forfait mensuel vaut quoi qu'on ait pointe, comme
  // sur la fiche officielle du serveur.
  const salHeures = totalH > 0 && txH > 0 ? totalH * txH : emp.salBase || 0;
  // Payé à l'heure, l'employé touche son indemnité de vacances chaque mois,
  // à part sur le décompte et soumise aux cotisations, comme sur la fiche
  // officielle du serveur.
  const indemVac =
    totalH > 0 && txH > 0 && +emp.tauxVac > 0
      ? Math.round(salHeures * (+emp.tauxVac / 100) * 100) / 100
      : 0;
  const brut = salHeures + indemVac;
  const joursRepas = [...new Set(moisRows.map((r) => r.k))].length;
  const indemRepas = Math.round((+emp.fraisRepas || 0) * joursRepas * 100) / 100;
  // Les retenues de la fiche officielle, interrupteurs « soumis à » compris :
  // une assurance à laquelle l'employé n'est pas soumis n'a pas de ligne. Les
  // taux sont ceux de l'entreprise pour l'année du relevé : servis par le
  // serveur dans l'espace de l'employé, lus dans ses réglages sur l'écran Équipe.
  const r = retenuesSalariales(
    brut,
    emp,
    emp.tauxRetenues || tauxRetenues(soc, +curYM.slice(0, 4)),
    emp.lignesPays || lignesDuPays(soc, +curYM.slice(0, 4)),
  );
  const { soumis, taux, lpp, dAvs, dAc, dAanp, dIjm, dLpp, dCanton } = r;
  // L'impôt à la source se retient sur le salaire, comme sur la fiche de
  // l'écran Équipe et le décompte PDF : le net est le même partout.
  const dIS = Math.round(brut * (+emp.is || 0)) / 100;
  const ded = dAvs + dAc + dAanp + dIjm + dLpp + dCanton + dIS;
  const lignesRetenues = (
    r.pays
      ? r.detail.map((d) => [d.libelle + ' (' + tauxEcrit(d.taux) + '%)', d.montant])
      : [
          soumis.avs && ['AVS / AI / APG (' + tauxEcrit(taux.avs) + '%)', dAvs],
          soumis.ac && ['Assurance chômage AC (' + tauxEcrit(taux.ac) + '%)', dAc],
          soumis.aanp && ['Assurance accident AANP (' + tauxEcrit(taux.aanp) + '%)', dAanp],
          dIjm > 0 && ['Indemnités journalières maladie IJM (' + tauxEcrit(taux.ijm) + '%)', dIjm],
          dCanton > 0 && ['Autres cotisations sociales (' + tauxEcrit(taux.canton) + '%)', dCanton],
          soumis.lpp && ['Prévoyance LPP 2e pilier (' + lpp + '%)', dLpp],
        ].filter(Boolean)
  ).concat(dIS > 0 ? [['Impôt à la source (' + (+emp.is || 0) + '%)', dIS]] : []);
  const net = brut - ded + indemRepas;
  const moisLabel = (ym) => {
    if (!ym) return '';
    const z = ym.split('-');
    return new Date(+z[0], +z[1] - 1, 1).toLocaleDateString(localeDates(), {
      month: 'long',
      year: 'numeric',
    });
  };
  const optMois = moisDispo.length ? moisDispo : [curYM];
  return (
    <div
      className="doc-root"
      style={{
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: '#4a4e57',
      }}
    >
      <style>
        {
          '@media(max-width:520px){.a4-wrap{padding:10px 6px!important}}@media print{.qr-scale-inner{transform:none!important;width:auto!important}.qr-scale-wrap{height:auto!important;overflow:visible!important}@page{size:A4;margin:0}html,body{background:#fff!important;margin:0!important;padding:0!important}.no-print{display:none!important}.doc-root{height:auto!important;overflow:visible!important;background:#fff!important}.a4-wrap{margin:0!important;padding:0!important;background:#fff!important}.a4-sheet{box-shadow:none!important;margin:0!important;width:210mm!important;max-width:210mm!important;min-height:auto!important;padding:10mm 12mm!important;zoom:.86}.releve-jours td,.releve-jours th{padding-top:3px!important;padding-bottom:3px!important}tr{page-break-inside:avoid}}'
        }
      </style>
      <div
        className="no-print"
        style={{
          background: accInk,
          color: '#fff',
          padding: '12px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          position: 'sticky',
          top: 0,
          zIndex: 5,
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          ‹ Retour
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.7)' }}>Mois :</span>
          <select
            value={curYM}
            onChange={(e) => setMoisSel(e.target.value)}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '7px 10px',
              fontSize: 11,
              fontWeight: 700,
              background: '#fff',
              color: accInk,
              cursor: 'pointer',
            }}
          >
            {optMois.map((ym) => (
              <option key={ym} value={ym}>
                {moisLabel(ym)}
              </option>
            ))}
          </select>
          <button
            onClick={printDoc}
            style={{
              background: C.green,
              border: 'none',
              color: '#fff',
              borderRadius: 8,
              padding: '8px 11px',
              fontSize: 10.5,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            📄 PDF
          </button>
          <button
            onClick={printDoc}
            style={{
              background: 'rgba(255,255,255,.2)',
              border: '1px solid rgba(255,255,255,.45)',
              color: '#fff',
              borderRadius: 8,
              padding: '8px 11px',
              fontSize: 10.5,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            🖨 Impr.
          </button>
        </div>
      </div>
      <div
        className="no-print"
        style={{
          background: '#eceef1',
          color: '#555',
          padding: '7px 14px',
          fontSize: 9,
          textAlign: 'center',
          lineHeight: 1.4,
        }}
      >
        📱 iPhone : « PDF » → sur l'aperçu, bouton Partager (carré + flèche) → « Enregistrer dans
        Fichiers ». · 💻 Ordinateur : « Imprimer » → « Enregistrer en PDF ».
      </div>
      <div
        className="a4-wrap"
        style={{ padding: '18px 12px', maxWidth: 'calc(210mm + 24px)', margin: '0 auto' }}
      >
        {/* La feuille garde sa largeur A4 et se réduit sur le téléphone, comme
            le PDF : aucune colonne n'est coupée à droite. */}
        <ScaleFit baseWidth={794}>
          <div
            className="a4-sheet"
            style={{
              width: '210mm',
              minHeight: '297mm',
              background: '#fff',
              color: '#0F1A2E',
              boxSizing: 'border-box',
              padding: '15mm 14mm',
              boxShadow: '0 6px 24px rgba(0,0,0,.35)',
              fontFamily: "'Helvetica Neue',Arial,sans-serif",
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '12px',
                paddingBottom: 14,
                borderBottom: '2.5px solid ' + accInk,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
              >
                {soc?.logo || SOC.logo ? (
                  <img
                    src={soc?.logo || SOC.logo}
                    style={{ width: 58, height: 58, objectFit: 'contain' }}
                    alt=""
                  />
                ) : (
                  <div
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 10,
                      background: accInk,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 900,
                      fontSize: 24,
                    }}
                  >
                    {(soc?.nom || SOC.nom).charAt(0)}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#0F1A2E' }}>
                    {soc?.nom || SOC.nom}
                  </div>
                  <div style={{ fontSize: 10, color: '#5a6270', marginTop: 3, lineHeight: 1.5 }}>
                    {soc?.adr || SOC.adr}
                    <br />
                    {soc?.npa || SOC.npa}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    display: 'inline-block',
                    background: accInk,
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 13,
                    letterSpacing: 0.5,
                    padding: '7px 13px',
                    borderRadius: 6,
                  }}
                >
                  RELEVÉ MENSUEL
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#3a4152',
                    marginTop: 8,
                    fontWeight: 700,
                    textTransform: 'capitalize',
                  }}
                >
                  {moisLabel(curYM)}
                </div>
                <div style={{ fontSize: 10, color: '#3a4152', marginTop: 2 }}>
                  {[emp.prenom, emp.nom].filter(Boolean).join(' ') || emp.nom}
                  {emp.role ? ' · ' + emp.role : ''}
                </div>
                <div style={{ fontSize: 9, color: '#8a909c', marginTop: 2 }}>
                  Émis le {dateEmis}
                </div>
              </div>
            </div>
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 800,
                color: accInk,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                margin: '18px 0 8px',
              }}
            >
              Détail jour par jour
            </div>
            {lignesJour.length === 0 ? (
              <div style={{ fontSize: 11, color: '#8a909c', padding: '14px 0' }}>
                Aucune heure pointée pour ce mois.
              </div>
            ) : (
              <table
                className="releve-jours"
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 10,
                  tableLayout: 'fixed',
                }}
              >
                <colgroup>
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '35%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: accInk, color: '#fff' }}>
                    <th style={{ textAlign: 'left', padding: '7px 8px', fontSize: 9 }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '7px 8px', fontSize: 9 }}>
                      {avecLieux ? tr('Lieu') : titreLieu}
                    </th>
                    <th style={{ textAlign: 'center', padding: '7px 8px', fontSize: 9 }}>
                      Horaire
                    </th>
                    <th style={{ textAlign: 'left', padding: '7px 8px', fontSize: 9 }}>Travaux</th>
                    <th style={{ textAlign: 'right', padding: '7px 8px', fontSize: 9 }}>Heures</th>
                    <th style={{ textAlign: 'right', padding: '7px 8px', fontSize: 9 }}>Écart</th>
                  </tr>
                </thead>
                <tbody>
                  {lignesJour.map((r, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: '1px solid #eef0f3',
                        // Un jour ouvrable sans heures se voit d'un coup d'œil.
                        color: r.vide ? '#a0a6b1' : undefined,
                        background: r.repos ? '#f7f8fa' : undefined,
                      }}
                    >
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 26,
                            textTransform: 'capitalize',
                          }}
                        >
                          {jourCourt(r)}
                        </span>{' '}
                        {r.date}
                      </td>
                      <td style={{ padding: '6px 8px', overflowWrap: 'anywhere' }}>{r.chantier}</td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'center',
                          color: '#5a6270',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.e ? r.e + (r.s ? '→' + r.s : '') : '—'}
                        {r.pause > 0 && (
                          <div style={{ fontSize: 8, color: '#8a909c' }}>
                            {tr('Pause')} {fmtH(r.pause / 60)}
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          color: '#5a6270',
                          fontSize: 9,
                          lineHeight: 1.4,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {r.note || '—'}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'right',
                          fontWeight: 800,
                          color: C.teal,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.vide ? '—' : fmtH(r.h)}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'right',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          color:
                            (bilan.parJour[r.k] || 0) > 0
                              ? C.green
                              : (bilan.parJour[r.k] || 0) < 0
                                ? C.amber
                                : '#8a909c',
                        }}
                      >
                        {dernierDuJour(i) && bilan.parJour[r.k] != null
                          ? fmtEcart(bilan.parJour[r.k])
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '10px 8px',
                fontSize: 12.5,
                fontWeight: 800,
                borderTop: '2px solid ' + accInk,
                marginTop: 2,
              }}
            >
              <span>Total des heures du mois</span>
              <span style={{ color: C.teal }}>{fmtH(totalH)}</span>
            </div>
            {bilan.jours > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 8,
                  padding: '4px 0 6px',
                  fontSize: 10,
                }}
              >
                <div>
                  Prévu au contrat
                  <br />
                  <b>{fmtH(bilan.prevu)}</b>{' '}
                  <span style={{ color: '#8a909c' }}>
                    ({bilan.jours} j × {fmtH(heuresDuContrat(emp))})
                  </span>
                </div>
                <div style={{ color: C.green }}>
                  Heures sup
                  <br />
                  <b>{fmtEcart(bilan.sup)}</b>
                </div>
                <div style={{ color: C.amber }}>
                  Heures en moins
                  <br />
                  <b>{bilan.moins > 0 ? fmtEcart(-bilan.moins) : '='}</b>
                </div>
              </div>
            )}
            {!vsSal && (
              <div
                style={{
                  fontSize: 10,
                  color: '#8a909c',
                  marginTop: 20,
                  padding: '12px 14px',
                  background: '#f7f8fa',
                  borderRadius: 10,
                  fontStyle: 'italic',
                }}
              >
                🔒 Le salaire n'est pas visible avec tes droits d'accès.
              </div>
            )}
            {vsSal && soc?.pays && soc.pays !== 'CH' && !r.pays && (
              <div
                style={{
                  marginTop: 20,
                  padding: '11px 13px',
                  background: '#fff4e5',
                  border: '1px solid #f0a500',
                  borderRadius: 10,
                  fontSize: 9.5,
                  color: '#8a5a00',
                  lineHeight: 1.6,
                }}
              >
                ⚠️ <b>Décompte de paie détaillé conçu pour la Suisse.</b> Pour{' '}
                {(PAYS_BY[soc.pays] && PAYS_BY[soc.pays].nom) || soc.pays}, les libellés et taux
                ci-dessous (AVS, AC, LAA, LPP) sont{' '}
                <b>suisses et donnés à titre indicatif uniquement</b>. Les charges sociales de votre
                pays doivent être configurées et validées par un comptable/fiduciaire local avant
                tout usage officiel.
              </div>
            )}
            {vsSal && (
              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  color: accInk,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  margin: '22px 0 8px',
                }}
              >
                Salaire du mois
              </div>
            )}
            {vsSal && (
              <div style={{ border: '1px solid #e2e5ea', borderRadius: 10, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '9px 13px',
                    fontSize: 11,
                    background: '#fafbfc',
                  }}
                >
                  <span>
                    Salaire brut
                    {totalH > 0
                      ? ' (' + fmtH(totalH) + ' × ' + DEVISE + ' ' + fmt(txH) + ')'
                      : ' (forfait)'}
                  </span>
                  <b>
                    {DEVISE} {fmt(salHeures)}
                  </b>
                </div>
                {indemVac > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '6px 13px',
                      fontSize: 10,
                      borderTop: '1px solid #eef0f3',
                    }}
                  >
                    <span>{`Indemnité de vacances (${tauxEcrit(+emp.tauxVac)}%)`}</span>
                    <b>
                      {DEVISE} {fmt(indemVac)}
                    </b>
                  </div>
                )}
                <div style={{ padding: '7px 13px', borderTop: '1px solid #eef0f3' }}>
                  {lignesRetenues.map(([libelle, montant]) => (
                    <div
                      key={libelle}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 9.5,
                        color: '#5a6270',
                        padding: '2px 0',
                      }}
                    >
                      <span>{libelle}</span>
                      <span style={{ color: '#c0392b' }}>
                        − {DEVISE} {fmt(montant)}
                      </span>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 14px',
                    background: accInk,
                    color: '#fff',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 800 }}>Net à payer</div>
                    <div style={{ fontSize: 8.5, color: 'rgba(255,255,255,.8)' }}>
                      Salaire brut moins déductions légales
                    </div>
                  </div>
                  <div style={{ fontSize: 21, fontWeight: 900 }}>
                    {DEVISE} {fmt(net)}
                  </div>
                </div>
              </div>
            )}
            {vsSal && r.pays && r.employeur.length > 0 && (
              <div
                style={{
                  marginTop: 10,
                  border: '1px solid #e2e5ea',
                  borderRadius: 10,
                  padding: '8px 13px',
                }}
              >
                <div style={{ fontSize: 9.5, fontWeight: 800, color: accInk, marginBottom: 4 }}>
                  Charges de l'employeur
                </div>
                {r.employeur.map((c) => (
                  <div
                    key={c.libelle}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 9.5,
                      color: '#5a6270',
                      padding: '2px 0',
                    }}
                  >
                    <span>
                      {c.libelle} ({tauxEcrit(c.taux)}%)
                    </span>
                    <span>
                      {DEVISE} {fmt(c.montant)}
                    </span>
                  </div>
                ))}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 10.5,
                    fontWeight: 800,
                    borderTop: '1px solid #eef0f3',
                    marginTop: 4,
                    paddingTop: 5,
                  }}
                >
                  <span>Coût total pour l'employeur</span>
                  <span>
                    {DEVISE} {fmt(brut + r.totalEmployeur)}
                  </span>
                </div>
              </div>
            )}
            <div
              style={{
                marginTop: 34,
                paddingTop: 14,
                borderTop: '1px solid #dde1e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
              }}
            >
              <div style={{ fontSize: 8.5, color: '#a0a6b0', lineHeight: 1.6, maxWidth: '58%' }}>
                Document indicatif généré par SwissTech le {dateEmis}. Les taux et montants doivent
                être validés par votre fiduciaire ou caisse de compensation.
              </div>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    borderTop: '1px solid #8a909c',
                    width: 170,
                    paddingTop: 5,
                    fontSize: 9,
                    color: '#8a909c',
                  }}
                >
                  Signature &amp; timbre employeur
                </div>
              </div>
            </div>
          </div>
        </ScaleFit>
      </div>
    </div>
  );
}
